import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as React from 'react';

import dropdownChevron from '../../assets/images/dropdown-chevron.svg';
import { Button } from '../../atoms/Button';
import { Search } from '../../atoms/Search';
import { cn } from '../../lib/utils';

export type FilterDropdownSelectionMode = 'checkbox' | 'radio';

export interface FilterDropdownOption {
  value: string;
  label: string;
  /** Optional inline glyph rendered before the label. */
  icon?: React.ReactNode;
  /** Optional count suffix rendered as `Label (count)`. */
  count?: number;
}

export interface FilterDropdownSortOption {
  value: string;
  label: string;
}

/**
 * Width values documented in Figma (`289:48315`/`48600`/`49244`/`49374`/`41026`).
 * Applied as an inline `min-width` style on the menu content node so that
 * tailwind-merge / arbitrary-class collisions don't matter at the test level.
 * A minimum (not a fixed width) because option labels are discovered per-tenant
 * data — a label longer than the Figma frame must widen the popover, not clip.
 */
export type FilterDropdownWidth = 184 | 225 | 255;

export interface FilterDropdownContentProps {
  selectionMode: FilterDropdownSelectionMode;
  options: ReadonlyArray<FilterDropdownOption>;

  /** `string` for radio mode, `string[]` for checkbox mode. */
  value: string | readonly string[];
  onChange: (next: string | readonly string[]) => void;

  /** Optional Sort section above the filter list. */
  sortOptions?: ReadonlyArray<FilterDropdownSortOption>;
  sortValue?: string;
  onSortChange?: (value: string) => void;

  /** Show search input above options. */
  showSearch?: boolean;
  searchPlaceholder?: string;

  /** Show a "Clear Selection" link at the section header. */
  showClearSelection?: boolean;
  /** Fired when the Clear Selection link is clicked. */
  onClearSelection?: () => void;

  /** Show a "Select All" link at the section header (Figma 5:12561). */
  showSelectAll?: boolean;
  /** Fired when the Select All link is clicked. */
  onSelectAll?: () => void;

  /** Show a footer Apply button. Required for committed-on-apply selection. */
  showApply?: boolean;
  onApply?: () => void;

  /** Configurable popover width — Figma documents 184/225/255. */
  width?: FilterDropdownWidth;

  /** Slot for date-range row or other custom content above the option list. */
  extraContent?: React.ReactNode;

  /** Optional className appended to the content root. */
  className?: string;
}

export interface FilterDropdownProps extends FilterDropdownContentProps {
  /** Trigger label shown next to the chevron. */
  triggerLabel?: string;
  /** Optional leading icon on the trigger. */
  triggerIcon?: React.ReactNode;
  /** Allow callers to restyle the trigger. */
  triggerClassName?: string;
  /** Popover alignment relative to the trigger. Default 'start'. */
  align?: 'start' | 'center' | 'end';
  /** Stable selector for E2E tests; forwarded to the trigger button. */
  triggerTestId?: string;
}

function formatLabel(option: FilterDropdownOption): string {
  return typeof option.count === 'number'
    ? `${option.label} (${option.count.toLocaleString('en-US')})`
    : option.label;
}

function isCheckboxValue(
  mode: FilterDropdownSelectionMode,
  value: string | readonly string[]
): value is readonly string[] {
  return mode === 'checkbox' && Array.isArray(value);
}

/**
 * Renders the content portion of a FilterDropdown — search + optional Sort
 * section + filter options (radio or checkbox) + optional Apply footer. Mounts
 * inside any Radix DropdownMenu/Popover Content host. Use `FilterDropdown` for
 * the bundled chevron-trigger ergonomics.
 */
export function FilterDropdownContent({
  selectionMode,
  options,
  value,
  onChange,
  sortOptions,
  sortValue,
  onSortChange,
  showSearch = false,
  searchPlaceholder = 'Search',
  showClearSelection = false,
  onClearSelection,
  showSelectAll = false,
  onSelectAll,
  showApply = false,
  onApply,
  width,
  extraContent,
  className,
}: FilterDropdownContentProps) {
  const [searchQuery, setSearchQuery] = React.useState('');

  const filteredOptions = React.useMemo(() => {
    if (!showSearch || searchQuery.trim() === '') return options;
    const q = searchQuery.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, showSearch, searchQuery]);

  const checkboxValues: readonly string[] = isCheckboxValue(selectionMode, value)
    ? value
    : typeof value === 'string'
      ? [value]
      : [];

  const radioValue: string = selectionMode === 'radio' && typeof value === 'string' ? value : '';

  function handleCheckboxToggle(optValue: string) {
    const next = checkboxValues.includes(optValue)
      ? checkboxValues.filter((v) => v !== optValue)
      : [...checkboxValues, optValue];
    onChange(next);
  }

  return (
    <div
      className={cn('flex flex-col gap-3', className)}
      style={width ? { minWidth: `${width}px` } : undefined}
      role="presentation"
    >
      {/* Sort section */}
      {sortOptions && sortOptions.length > 0 && onSortChange ? (
        <div className="flex flex-col gap-2 border-b border-grey-200 pb-3">
          <div className="font-outfit text-[12px] font-medium leading-[1.4] text-grey-500">
            Sort
          </div>
          <DropdownMenuPrimitive.RadioGroup
            value={sortValue ?? ''}
            onValueChange={(v) => onSortChange(v)}
            className="flex flex-col gap-2"
          >
            {sortOptions.map((opt) => (
              <DropdownMenuPrimitive.RadioItem
                key={opt.value}
                value={opt.value}
                className={cn(
                  'group flex cursor-pointer items-center gap-1 outline-none',
                  'font-outfit text-[12px] font-normal leading-[1.4] text-[#525252]'
                )}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  <span className="flex h-3 w-3 items-center justify-center rounded-full border border-grey-400 bg-dawn-white group-data-[state=checked]:border-dawn-orange">
                    <DropdownMenuPrimitive.ItemIndicator>
                      <span className="block h-1.5 w-1.5 rounded-full bg-dawn-orange" />
                    </DropdownMenuPrimitive.ItemIndicator>
                  </span>
                </span>
                <span>{opt.label}</span>
              </DropdownMenuPrimitive.RadioItem>
            ))}
          </DropdownMenuPrimitive.RadioGroup>
        </div>
      ) : null}

      {/* Section header (Select All + Clear Selection links per Figma 5:12561) */}
      {(showSelectAll && onSelectAll) || (showClearSelection && onClearSelection) ? (
        <div className="flex w-full items-center justify-between font-outfit text-[12px] font-normal leading-[1.4] text-grey-400">
          {showSelectAll && onSelectAll ? (
            <button
              type="button"
              onClick={onSelectAll}
              className="cursor-pointer bg-transparent p-0 outline-none hover:text-dawn-orange focus-visible:text-dawn-orange"
            >
              Select All
            </button>
          ) : (
            <span aria-hidden="true" />
          )}
          {showClearSelection && onClearSelection ? (
            <button
              type="button"
              onClick={onClearSelection}
              className="cursor-pointer bg-transparent p-0 outline-none hover:text-dawn-orange focus-visible:text-dawn-orange"
            >
              Clear Selection
            </button>
          ) : (
            <span aria-hidden="true" />
          )}
        </div>
      ) : null}

      {/* Optional search */}
      {showSearch ? (
        <Search
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={searchPlaceholder}
          className="w-full"
        />
      ) : null}

      {/* Extra-content slot (e.g., date-range row) */}
      {extraContent}

      {/* Options list */}
      {selectionMode === 'radio' ? (
        <DropdownMenuPrimitive.RadioGroup
          value={radioValue}
          onValueChange={(v) => onChange(v)}
          className="flex max-h-72 flex-col gap-3 overflow-y-auto"
        >
          {filteredOptions.map((option) => (
            <DropdownMenuPrimitive.RadioItem
              key={option.value}
              value={option.value}
              className={cn(
                'group flex cursor-pointer items-center gap-1 outline-none',
                'font-outfit text-[12px] font-normal leading-[1.4] text-[#525252]'
              )}
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                <span className="flex h-3 w-3 items-center justify-center rounded-full border border-grey-400 bg-dawn-white group-data-[state=checked]:border-dawn-orange">
                  <DropdownMenuPrimitive.ItemIndicator>
                    <span className="block h-1.5 w-1.5 rounded-full bg-dawn-orange" />
                  </DropdownMenuPrimitive.ItemIndicator>
                </span>
              </span>
              {option.icon ? (
                // Sized by content, not a fixed 16px box: badge glyphs are
                // 18px, and composite badges (e.g. External Human = user+star)
                // are wider — a fixed box centers them over the checkbox.
                <span className="inline-flex shrink-0 items-center justify-center">
                  {option.icon}
                </span>
              ) : null}
              <span className="whitespace-nowrap">{formatLabel(option)}</span>
            </DropdownMenuPrimitive.RadioItem>
          ))}
        </DropdownMenuPrimitive.RadioGroup>
      ) : (
        // max-h + scroll: option lists are discovered per-tenant data and can
        // hold the full vocabulary (50+ file types); the popover must not
        // outgrow the viewport.
        <div className="flex max-h-72 flex-col gap-3 overflow-y-auto">
          {filteredOptions.map((option) => {
            const isChecked = checkboxValues.includes(option.value);
            return (
              <DropdownMenuPrimitive.CheckboxItem
                key={option.value}
                checked={isChecked}
                onCheckedChange={() => handleCheckboxToggle(option.value)}
                onSelect={(e) => e.preventDefault()}
                className={cn(
                  'group flex cursor-pointer items-center gap-1 outline-none',
                  'font-outfit text-[12px] font-normal leading-[1.4] text-[#525252]'
                )}
              >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                  <span
                    className={cn(
                      'inline-flex h-3 w-3 items-center justify-center rounded-[4px] border transition-colors',
                      isChecked
                        ? 'border-dawn-orange bg-dawn-orange'
                        : 'border-grey-400 bg-transparent'
                    )}
                  >
                    {isChecked ? (
                      <svg
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3"
                        aria-hidden="true"
                      >
                        <path
                          d="M2.5 6.25L4.83333 8.5L9.5 3.75"
                          stroke="#FFFFFF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : null}
                  </span>
                </span>
                {option.icon ? (
                  // Sized by content, not a fixed 16px box: badge glyphs are
                  // 18px, and composite badges (e.g. External Human = user+star)
                  // are wider — a fixed box centers them over the checkbox.
                  <span className="inline-flex shrink-0 items-center justify-center">
                    {option.icon}
                  </span>
                ) : null}
                <span className="whitespace-nowrap">{formatLabel(option)}</span>
              </DropdownMenuPrimitive.CheckboxItem>
            );
          })}
        </div>
      )}

      {/* Apply footer */}
      {showApply && onApply ? (
        <div className="flex justify-end pt-2">
          <Button
            type="button"
            size="sm"
            onClick={() => {
              onApply();
            }}
          >
            Apply
          </Button>
        </div>
      ) : null}
    </div>
  );
}

FilterDropdownContent.displayName = 'FilterDropdownContent';

/**
 * FilterDropdown — bundled chevron-trigger + FilterDropdownContent. Drop-in
 * convenience for callers that want the default trigger ergonomics. Custom
 * triggers should mount `FilterDropdownContent` inside their own Radix host.
 */
const FilterDropdown = React.forwardRef<HTMLButtonElement, FilterDropdownProps>(
  (
    {
      triggerLabel,
      triggerIcon,
      triggerClassName,
      align = 'start',
      width,
      triggerTestId,
      ...contentProps
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);

    // Compute selected label for the trigger when in radio mode.
    const selectedLabel = (() => {
      if (contentProps.selectionMode === 'radio' && typeof contentProps.value === 'string') {
        const opt = contentProps.options.find((o) => o.value === contentProps.value);
        return opt?.label ?? triggerLabel;
      }
      return triggerLabel;
    })();

    return (
      <DropdownMenuPrimitive.Root open={open} onOpenChange={setOpen}>
        <DropdownMenuPrimitive.Trigger
          ref={ref}
          data-testid={triggerTestId}
          className={cn(
            'inline-flex items-center gap-1 bg-transparent border-0 p-0 outline-none',
            'font-outfit text-[13px] font-normal leading-[1.3] text-dawn-black',
            'hover:opacity-80 focus-visible:opacity-80 cursor-pointer',
            triggerClassName
          )}
        >
          {triggerIcon ? <span className="flex shrink-0 items-center">{triggerIcon}</span> : null}
          <span>{selectedLabel}</span>
          <img
            src={dropdownChevron}
            alt=""
            className={cn(
              'h-[14px] w-[14px] shrink-0 transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
        </DropdownMenuPrimitive.Trigger>

        <DropdownMenuPrimitive.Portal>
          <DropdownMenuPrimitive.Content
            align={align}
            sideOffset={6}
            style={width ? { minWidth: `${width}px` } : undefined}
            className={cn(
              'z-50 rounded-[4px] border border-grey-200 bg-dawn-white p-5 shadow-md',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
            )}
          >
            <FilterDropdownContent {...contentProps} />
          </DropdownMenuPrimitive.Content>
        </DropdownMenuPrimitive.Portal>
      </DropdownMenuPrimitive.Root>
    );
  }
);

FilterDropdown.displayName = 'FilterDropdown';

export { FilterDropdown };
